import type { SelectedProduct, VehicleSectionV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import Section from "../../components/Section";
import { ContractVehicleInformation } from "./fields/ContractVehicleInformation";
import { GapInformation } from "./fields/GapInformation";
import { InsuranceInformation } from "./fields/InsuranceInformation";
import { VehicleServiceContractInformation } from "./fields/VehicleServiceContractInformation";

interface VehicleProperties {
  /** Vehicle details + insurance from the dashboard response. */
  readonly vehicle?: VehicleSectionV2;
  /** From `financial.tila.amountFinanced` on the dashboard response — lives outside `VehicleSection` itself. */
  readonly amountFinanced?: number;
  /** From `additionalProducts.vsc` — a deal has at most one VSC product in practice, so callers pass its first element. */
  readonly vsc?: SelectedProduct;
  /** From `additionalProducts.gap` — same "first element" convention as `vsc`. */
  readonly gap?: SelectedProduct;
  /**
   * Whether the non-`LabeledValue` fields below (everything except Year/Make/
   * Model/VIN/Color/Book Wholesale/Book Retail/Amount Financed) are typeable/
   * selectable. Defaults to `false` (today's fully read-only behavior) — no
   * condition has been decided yet for when Vehicle data should become
   * editable, so this is just the plumbing a caller can flip once one is.
   * Purely local UI state either way: the BFF's dashboard save endpoint
   * doesn't persist vehicle/financial/additionalProducts data, so there's no
   * save wiring here (unlike Signers).
   */
  readonly editable?: boolean;
  /** From `financial.vehicle.condition`; NEW makes Contract Vehicle's Total Selling Price editable. */
  readonly condition?: string;
  /**
   * Whether Contract Vehicle's Odometer Disc Mileage and CPO Date are typeable (Total Selling Price also
   * needs a NEW vehicle). Defaults to `editable`, so it fails safe to read-only until the caller resolves
   * the VLA-lock / role / deal-state / pre-approval gates.
   */
  readonly contractVehicleEditable?: boolean;
}

// ---------------------------------------------------------------------------
// Vehicle card — collapsible outer card (via Section/Accordion), same as
// Signers, with two rows of two-column subsections: Contract Vehicle |
// Insurance, then Vehicle Service Contract | GAP.
// ---------------------------------------------------------------------------
function Vehicle({
  vehicle,
  amountFinanced,
  vsc,
  gap,
  editable = false,
  condition,
  contractVehicleEditable = editable,
}: VehicleProperties) {
  const { t } = useTranslation();

  return (
    <Section title={t("vehicle.sectionTitle")}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 xl:grid-cols-2">
          <ContractVehicleInformation
            vehicle={vehicle}
            condition={condition}
            contractVehicleEditable={contractVehicleEditable}
            editable={editable}
          />
          <InsuranceInformation insurance={vehicle?.insurance} amountFinanced={amountFinanced} editable={editable} />
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 xl:grid-cols-2">
          <VehicleServiceContractInformation vsc={vsc} editable={editable} />
          <GapInformation gap={gap} editable={editable} />
        </div>
      </div>
    </Section>
  );
}

export default Vehicle;
